import React, { useCallback, useEffect } from "react";
import { WizardService } from "../../../services/WizardService";
import { Sdk } from "../../../types/sdk";
import {
  Breadcrumb,
  Button,
  Drawer,
  Layout,
  message,
  Descriptions,
  Row,
  Skeleton,
  Col,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { AdapterForm } from "./AdapterForm";
import { AppRoutes } from "../../../AppRoutes";
import { IntegrationTable } from "../integration/IntegrationTable";
import { SpmIntegrationTable } from "../integration/SpmIntegrationTable";
import {
  nativeIntegrationLabel,
  supportsSpm,
} from "../integration/platformIntegration";
import { Integration } from "../../../types/integration";
import {
  SpmIntegrationDraft,
  SpmIntegrationInfo,
} from "../../../types/spmIntegrationInfo";
import { IntegrationForm } from "../integration/IntegrationForm";
import { SpmIntegrationForm } from "../integration/SpmIntegrationForm";
import { Adapter, ConstraintV2, ConstraintV2Draft } from "../../../types/adapter";
import { DropdownAction } from "../../components/ColumnBuilder";
import { SdkList } from "../sdk/SdkList";
import { ConstraintV2Table } from "../constraints/ConstraintV2Table";
import { ConstraintV2Form } from "../constraints/ConstraintV2Form";
import { ResponsiveCard } from "../../components/ResponsiveCard";
import { SuggestedSdkList } from "../sdk/SuggestedSdkList";
import { PlatformIcon } from "../../components/PlatformIcon";

const { Content } = Layout;

// Backend error bodies vary by status: 400/404 are `{error: "<snake_case_code>", status}` (no
// `message`), while the newer 409 conflict responses add a human-readable `message` alongside
// `error`. **Corrected 2026-09-22** (double-review 2026-09-22 R7a): the previous version of this
// comment claimed all three statuses carry `message` — wrong; only 409 does. Preferring
// `data.message` and falling back to `data.error` is still correct either way (400/404 simply
// have no `data.message` to prefer), so no behavior change, just the comment.
const extractErrorMessage = (error: any): string =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Something went wrong";

export const AdapterViewPage = () => {
  const api = WizardService.Adapter;
  const navigate = useNavigate();

  const { id } = useParams();

  const [isAdapterLoading, setAdapterLoading] = React.useState<boolean>(false);
  const [isAdapterEditing, setAdapterEditing] = React.useState<boolean>(false);
  const [adapter, setAdapter] = React.useState<Adapter>();

  const [isIntegrationsLoading, setIntegrationsLoading] =
    React.useState<boolean>(false);
  const [isAddingIntegration, setAddingIntegration] =
    React.useState<boolean>(false);
  const [editingIntegrationId, setEditingIntegrationId] = React.useState<
    number | undefined
  >(undefined);
  const [integrations, setIntegrations] = React.useState<Integration[]>([]);

  // Rendered on iOS adapters only (see `supportsSpm`). An adapter-anchored SPM row is otherwise
  // visible nowhere but the /wizard/spm overview.
  // **Corrected 2026-09-22** (adapter full-CRUD task, "curated/lock switch" explicitly rejected):
  // this card used to be read-only display; it now supports add ("+" in the card header),
  // edit and delete on every row via `addSpmIntegration`/`updateSpmIntegration`/
  // `deleteSpmIntegration` below, same as the other two cards on this page.
  const [isSpmLoading, setSpmLoading] = React.useState<boolean>(false);
  const [isAddingSpm, setAddingSpm] = React.useState<boolean>(false);
  const [editingSpmId, setEditingSpmId] = React.useState<number | undefined>(
    undefined
  );
  const [spmIntegrations, setSpmIntegrations] = React.useState<
    SpmIntegrationInfo[]
  >([]);

  // constraints_v2 is authoritative post-cutover (SDK-679) — sole constraints UI on this page.
  const [isConstraintsV2Loading, setConstraintsV2Loading] =
    React.useState<boolean>(false);
  const [isAddingConstraintV2, setAddingConstraintV2] =
    React.useState<boolean>(false);
  const [editingConstraintId, setEditingConstraintId] = React.useState<
    number | undefined
  >(undefined);
  const [constraintsV2, setConstraintsV2] = React.useState<ConstraintV2[]>([]);

  const [isSdksLoading, setSdksLoading] = React.useState<boolean>(false);
  const [isAddingSdk, setAddingSdk] = React.useState<boolean>(false);
  const [sdks, setSdks] = React.useState<Sdk[]>([]);

  const saveAdapter = (record: Adapter) => {
    setAdapterLoading(true);
    let promise: Promise<any>;
    if (record.id !== undefined && record.id > 0) {
      promise = api.update(record.id, record);
    } else {
      promise = api.create(record);
    }

    promise
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => {
        setAdapterEditing(false);
        setAdapterLoading(false);
      });
  };

  const deleteIntegration = (integrationId: number) => {
    setIntegrationsLoading(true);
    api
      .removeIntegration(parseInt(id!), integrationId)
      .then(() => {
        // A modal bound to this row (via `editingIntegrationId`) must not stay open pointing at a
        // row that no longer exists — double-review 2026-09-22 R7b.
        setEditingIntegrationId((current) =>
          current === integrationId ? undefined : current
        );
        fetchIntegrations();
      })
      .catch((error) => {
        message.error(extractErrorMessage(error));
        setIntegrationsLoading(false);
      });
  };

  const addIntegration = (record: Integration) => {
    setIntegrationsLoading(true);
    api
      .addIntegration(parseInt(id!), record)
      .then(() => {
        setAddingIntegration(false);
        fetchIntegrations();
      })
      .catch((error) => {
        message.error(extractErrorMessage(error));
        setIntegrationsLoading(false);
      });
  };

  const updateIntegration = (integrationId: number, record: Integration) => {
    setIntegrationsLoading(true);
    api
      .updateIntegration(parseInt(id!), integrationId, record)
      .then(() => {
        setEditingIntegrationId(undefined);
        fetchIntegrations();
      })
      .catch((error) => {
        message.error(extractErrorMessage(error));
        setIntegrationsLoading(false);
      });
  };

  const deleteSdk = (sdkId: number) => {
    setSdksLoading(true);
    api
      .removeSdk(parseInt(id!), sdkId)
      .then(() => fetchSdks())
      .catch((error) => {
        message.error(error.message);
        setSdksLoading(false);
      });
  };

  const addSdk = (records: Sdk[]) => {
    setSdksLoading(true);
    Promise.all(records.map((record) => api.addSdk(parseInt(id!), record)))
      .then(() => fetchSdks())
      .catch((error) => {
        message.error(error.message);
        setSdksLoading(false);
      });
  };

  const deleteConstraintV2 = (constraintId: number) => {
    setConstraintsV2Loading(true);
    api
      .removeConstraintV2(parseInt(id!), constraintId)
      .then(() => {
        // Double-review 2026-09-22 R7b: don't leave an edit modal open on a row that was just
        // deleted (e.g. a second browser tab, or a quick delete right after opening edit).
        setEditingConstraintId((current) =>
          current === constraintId ? undefined : current
        );
        fetchConstraintsV2();
      })
      .catch((error) => {
        message.error(extractErrorMessage(error));
        setConstraintsV2Loading(false);
      });
  };

  const addConstraintV2 = (record: ConstraintV2Draft) => {
    setConstraintsV2Loading(true);
    api
      .addConstraintV2(parseInt(id!), record)
      .then(() => {
        setAddingConstraintV2(false);
        fetchConstraintsV2();
      })
      .catch((error) => {
        message.error(extractErrorMessage(error));
        setConstraintsV2Loading(false);
      });
  };

  const updateConstraintV2 = (constraintId: number, record: ConstraintV2Draft) => {
    setConstraintsV2Loading(true);
    api
      .updateConstraintV2(parseInt(id!), constraintId, record)
      .then(() => {
        setEditingConstraintId(undefined);
        fetchConstraintsV2();
      })
      .catch((error) => {
        message.error(extractErrorMessage(error));
        setConstraintsV2Loading(false);
      });
  };

  const addSpmIntegration = (record: SpmIntegrationDraft) => {
    setSpmLoading(true);
    api
      .addSpmIntegration(parseInt(id!), record)
      .then(() => {
        setAddingSpm(false);
        fetchSpmIntegrations();
      })
      .catch((error) => {
        message.error(extractErrorMessage(error));
        setSpmLoading(false);
      });
  };

  const updateSpmIntegration = (spmId: number, record: SpmIntegrationDraft) => {
    setSpmLoading(true);
    api
      .updateSpmIntegration(parseInt(id!), spmId, record)
      .then(() => {
        setEditingSpmId(undefined);
        fetchSpmIntegrations();
      })
      .catch((error) => {
        message.error(extractErrorMessage(error));
        setSpmLoading(false);
      });
  };

  const deleteSpmIntegration = (spmId: number) => {
    setSpmLoading(true);
    api
      .removeSpmIntegration(parseInt(id!), spmId)
      .then(() => {
        // Double-review 2026-09-22 R7b: same as the constraint/integration deletes above.
        setEditingSpmId((current) => (current === spmId ? undefined : current));
        fetchSpmIntegrations();
      })
      .catch((error) => {
        message.error(extractErrorMessage(error));
        setSpmLoading(false);
      });
  };

  // Callbacks
  const fetch = useCallback(() => {
    setAdapterLoading(true);
    api
      .one(parseInt(id!))
      .then((sdk) => setAdapter(sdk))
      .catch((error) => message.error(error.message))
      .finally(() => setAdapterLoading(false));
  }, [api, id]);

  const fetchIntegrations = useCallback(() => {
    setIntegrationsLoading(true);
    api
      .integration(parseInt(id!))
      .then((integrations) => setIntegrations(integrations || []))
      .catch((error) => message.error(error.message))
      .finally(() => setIntegrationsLoading(false));
  }, [api, id]);

  const fetchSpmIntegrations = useCallback(() => {
    setSpmLoading(true);
    api
      .spmIntegrations(parseInt(id!))
      .then((records) => setSpmIntegrations(records || []))
      .catch((error) => message.error(error.message))
      .finally(() => setSpmLoading(false));
  }, [api, id]);

  const fetchSdks = useCallback(() => {
    setSdksLoading(true);
    api
      .sdks(parseInt(id!))
      .then((sdks) => setSdks(sdks || []))
      .catch((error) => message.error(error.message))
      .finally(() => setSdksLoading(false));
  }, [api, id]);

  const fetchConstraintsV2 = useCallback(() => {
    setConstraintsV2Loading(true);
    api
      .constraintsV2(parseInt(id!))
      .then((records) => setConstraintsV2(records || []))
      .catch((error) => message.error(extractErrorMessage(error)))
      .finally(() => setConstraintsV2Loading(false));
  }, [api, id]);

  // Hooks
  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  // Gated on the platform, not fired unconditionally: an Android adapter can never have SPM rows,
  // so the request is guaranteed-empty and the card is not rendered anyway. `adapter` arrives
  // async, so this correctly no-ops until the platform is known.
  useEffect(() => {
    if (supportsSpm(adapter?.platform)) {
      fetchSpmIntegrations();
    }
  }, [fetchSpmIntegrations, adapter?.platform]);

  useEffect(() => {
    fetchSdks();
  }, [fetchSdks]);

  useEffect(() => {
    fetchConstraintsV2();
  }, [fetchConstraintsV2]);

  const formattedDate = (date?: string) => {
    if (date) {
      return new Date(date).toDateString();
    }
  };

  return (
    <Layout>
      <Content>
        <Row style={{ margin: "1rem 0" }}>
          <Breadcrumb
            items={[
              {
                href: "/" + AppRoutes.Wizard.AdapterList,
                title: "Adapter Releases",
                onClick: () => {
                  navigate(-1);
                },
              },
              {
                title: "Adapter #" + id,
              },
            ]}
          />
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <ResponsiveCard
              title="Adapter"
              contentPadding="1rem"
              extra={
                <Button
                  type="text"
                  icon={<EditOutlined  />}
                  onClick={() => setAdapterEditing(true)}
                />
              }
            >
              {isAdapterLoading ? (
                <Skeleton active />
              ) : (
                <Descriptions
                  column={2}
                  items={[
                    {
                      key: "1",
                      label: "OS",
                      children: adapter && (
                        <PlatformIcon platform={adapter.platform} />
                      ),
                    },
                    { key: "3", label: "Status", children: adapter?.status },
                    { key: "4", label: "Version", children: adapter?.version },
                    {
                      key: "5",
                      label: "Created",
                      children: formattedDate(adapter?.createdAt),
                    },
                    {
                      key: "6",
                      label: "Updated",
                      children: formattedDate(adapter?.updatedAt),
                    },
                  ]}
                />
              )}
            </ResponsiveCard>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <ResponsiveCard
              title={`Integration — ${nativeIntegrationLabel(adapter?.platform)}`}
              extra={
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => setAddingIntegration(true)}
                />
              }
            >
              <IntegrationTable
                loading={isIntegrationsLoading}
                records={integrations}
                onEdit={(integrationId) => setEditingIntegrationId(integrationId)}
                onDelete={deleteIntegration}
              />
            </ResponsiveCard>
            {supportsSpm(adapter?.platform) && (
              <ResponsiveCard
                title={"Integration — SPM"}
                style={{ marginTop: "1rem" }}
                extra={
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    aria-label="Add SPM integration"
                    onClick={() => setAddingSpm(true)}
                  />
                }
              >
                <SpmIntegrationTable
                  loading={isSpmLoading}
                  records={spmIntegrations}
                  onEdit={(spmId) => setEditingSpmId(spmId)}
                  onDelete={deleteSpmIntegration}
                />
              </ResponsiveCard>
            )}
          </Col>
          <Col span={12}>
            <ResponsiveCard
              title={"Constraints"}
              extra={
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  aria-label="Add constraint"
                  onClick={() => setAddingConstraintV2(true)}
                />
              }
            >
              <ConstraintV2Table
                loading={isConstraintsV2Loading}
                records={constraintsV2}
                onEdit={(constraintId) => setEditingConstraintId(constraintId)}
                onDelete={deleteConstraintV2}
              />
            </ResponsiveCard>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <ResponsiveCard
              title={"Adaptees SDK"}
              extra={
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => setAddingSdk(true)}
                />
              }
            >
              <SdkList
                loading={isSdksLoading}
                records={sdks}
                actions={[DropdownAction.VIEW, DropdownAction.DELETE]}
                removeRecord={deleteSdk}
                viewRecord={(id) =>
                  navigate(
                    "/" + AppRoutes.Wizard.Sdk.replace(":id", id.toString())
                  )
                }
                pagination={false}
                editable
              />
            </ResponsiveCard>
          </Col>
        </Row>
        <IntegrationForm
          open={isAddingIntegration || editingIntegrationId !== undefined}
          record={
            editingIntegrationId !== undefined
              ? integrations.find((r) => r.id === editingIntegrationId)
              : undefined
          }
          onCancel={() => {
            setAddingIntegration(false);
            setEditingIntegrationId(undefined);
          }}
          onSave={(record) =>
            editingIntegrationId !== undefined
              ? updateIntegration(editingIntegrationId, record)
              : addIntegration(record)
          }
        />
        <SpmIntegrationForm
          open={isAddingSpm || editingSpmId !== undefined}
          record={
            editingSpmId !== undefined
              ? spmIntegrations.find((r) => r.id === editingSpmId)
              : undefined
          }
          onCancel={() => {
            setAddingSpm(false);
            setEditingSpmId(undefined);
          }}
          onSave={(record) =>
            editingSpmId !== undefined
              ? updateSpmIntegration(editingSpmId, record)
              : addSpmIntegration(record)
          }
        />
        <SuggestedSdkList
          adapterId={adapter?.id}
          open={isAddingSdk}
          onSdksSelected={(records) => addSdk(records)}
          onClose={() => setAddingSdk(false)}
        />
        <ConstraintV2Form
          open={isAddingConstraintV2 || editingConstraintId !== undefined}
          record={
            editingConstraintId !== undefined
              ? constraintsV2.find((r) => r.id === editingConstraintId)
              : undefined
          }
          platform={adapter?.platform}
          onCancel={() => {
            setAddingConstraintV2(false);
            setEditingConstraintId(undefined);
          }}
          onSave={(record) =>
            editingConstraintId !== undefined
              ? updateConstraintV2(editingConstraintId, record)
              : addConstraintV2(record)
          }
        />
        <Drawer
          title={"Edit Sdk Release #" + id}
          width={600}
          onClose={(_) => setAdapterEditing(false)}
          open={isAdapterEditing}
          styles={{
            body: {
              paddingBottom: 80,
            },
          }}
        >
          <AdapterForm
            adapter={adapter}
            isLoading={isAdapterLoading}
            onFinish={saveAdapter}
          />
        </Drawer>
      </Content>
    </Layout>
  );
};
